import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { useToast } from '@/contexts/ToastContext'
import { adminUserService } from '@/services/adminUserService'
import { User } from '@/types/user'

const AdminUserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { showToast } = useToast()

  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return

    let cancelled = false
    setLoading(true)
    adminUserService
      .getUserById(id)
      .then((result) => {
        if (!cancelled) setUser(result)
      })
      .catch((error) => {
        if (!cancelled) {
          showToast({
            type: 'error',
            message: error instanceof Error ? error.message : 'Failed to fetch user'
          })
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  return (
    <div>
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm">
        <ArrowLeft className="w-4 h-4" />
        Back to users
      </Link>

      <h2 className="text-2xl font-bold text-gray-900 mt-4">User detail</h2>

      <Card className="mt-6">
        <CardBody>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
          ) : !user ? (
            <p className="text-center text-gray-500 py-8">User not found.</p>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-gray-500">Name</dt>
                <dd className="text-gray-900 font-medium">
                  {user.firstName} {user.lastName}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Email</dt>
                <dd className="text-gray-900">{user.email}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Role</dt>
                <dd className="text-gray-900 capitalize">{user.role}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Status</dt>
                <dd className="text-gray-900 capitalize">{user.status || 'active'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Verified</dt>
                <dd className="text-gray-900">{user.isVerified ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Onboarding complete</dt>
                <dd className="text-gray-900">{user.isOnboardingComplete ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Joined</dt>
                <dd className="text-gray-900">{new Date(user.createdAt).toLocaleDateString()}</dd>
              </div>
            </dl>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default AdminUserDetailPage
